<template>
  <is-loading :if="$storeData.productsAreLoading">
    <has-request-error
      :has-error="$storeData.productsRequestError"
      :is-loading="$storeData.productsAreReloading"
      @retry="reloadData"
    >
      <div>
        <p class="u-heading has-padding-bottom-50 has-border-bottom">
          <i18n v-if="!!$storeData.searchValue" path="_.search_results" />
          <span v-else>{{ categoryName }}</span>
        </p>
        <b-field v-if="!$storeData.hasDacWidget">
          <b-input
            v-model="$storeData.searchValue"
            expanded
            class="has-margin-bottom-100"
            :placeholder="$t('_placeholder.product_search')"
            :icon-right="$storeData.searchValue ? 'close-circle' : null"
            :icon-right-clickable="!!$storeData.searchValue"
            @icon-right-click="searchByName('')"
            @input="searchByNameDebounce"
          />
        </b-field>

        <template v-if="showDescription">
          <u-line-clamp
            class="has-margin-bottom-100 has-text-grey is-size-7"
            :force-open="!truncateDesc"
          >
            <u-markdown>{{ categoryDescription }}</u-markdown>
          </u-line-clamp>
        </template>

        <template v-if="showNoResults">
          <i18n path="_.no_product_matching" tag="p">
            <template #value>
              <strong>{{ $storeData.searchValue }}</strong>
            </template>
          </i18n>
        </template>
        <template v-if="showNoSellableProducts">
          <i18n path="_.no_sellable_products" tag="p" />
        </template>
        <upm-dac
          v-if="$storeData.hasDacWidget"
          :key="`category-${$storeData.selectedCategory.id}`"
          :auth-token="authToken"
          :account-id="basketData.accountId"
          :basket-id="basketData.basketId"
          :brand-id="basketData.basketBrandId"
          :coupons="basketData.coupons"
          :currency-id="basketData.currencyId"
          :on-add-to-basket="dacAddToBasket"
          class="is-paddingless"
          @unauthorised="$store.dispatch('auth/refreshAccessToken')"
        >
          <p class="is-size-8 has-text-grey has-margin-top-100">
            *
            <i18n
              tag="em"
              :path="
                basketData.brandTaxType
                  ? '_sentence.prices_include_tax_msg'
                  : '_sentence.prices_exclude_tax_msg'
              "
            />
            <i18n
              tag="em"
              path="_sentence.product.renewal_prices_exclude_discounts_msg"
            />
          </p>
        </upm-dac>
        <template v-else>
          <shop-products-list
            :items="items"
            :layout="itemsLayout"
            :is-loading="
              $storeData.productsAreLoading || $storeData.productsAreFiltering
            "
            :is-loading-more="$storeData.productsAreLoadingMore"
            :has-more="$storeData.hasMore"
            :category-shortcut="true"
            @addToBasket="addToBasket"
            @loadMore="$storeMethods.loadMore"
          />
          <p
            v-if="!$_.isEmpty($storeData.products)"
            class="is-size-8 has-text-grey has-margin-top-100"
          >
            *
            <i18n
              tag="em"
              :path="
                basketData.brandTaxType
                  ? '_sentence.prices_include_tax_msg'
                  : '_sentence.prices_exclude_tax_msg'
              "
            />
          </p>
        </template>
      </div>
    </has-request-error>
  </is-loading>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import _ from "@/boot/lodash";
import { UpmDac } from "@upmind-widgets/dac";
import Context from "@/mixins/context";
import { ShopItemsLayout } from "@/data/shop/enums";
import { BrandConfigKeys, QUERY_PARAMS } from "@/data/constants";
import { getCategoryRoute } from "@/data/shop/utils";
import type {
  IStoreProviderData,
  IStoreProviderMethods
} from "@/models/providers/basket/store";
import type {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import type {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import type { IShopProviderMethods } from "@/models/providers/basket/shop";
import type { IProduct } from "@/models/products";

export default defineComponent({
  name: "ProductListWrapper",
  components: {
    UpmDac,
    "shop-products-list": () =>
      import("@/components/app/global/baskets/basket/shop/shopProductsList.vue")
  },
  mixins: [Context],
  inject: {
    $storeProviderData: {
      from: "storeProviderData",
      type: Function as PropType<IStoreProviderData>
    },
    $storeMethods: {
      from: "storeProviderMethods",
      type: Object as PropType<IStoreProviderMethods>
    },
    $basketProviderData: {
      from: "basketProviderData",
      type: Function as PropType<IBasketProviderData>
    },
    $basketMethods: {
      from: "basketProviderMethods",
      type: Object as PropType<IBasketProviderMethods>
    },
    $provisionProviderMethods: {
      from: "provisionProviderMethods",
      type: Object as PropType<IProvisionProviderMethods>
    },
    $provisionProviderData: {
      from: "provisionProviderData",
      type: Function as PropType<IProvisionProviderData>
    },
    $shopMethods: {
      from: "shopProviderMethods",
      type: Object as PropType<IShopProviderMethods>
    }
  },
  data: () => ({
    searchByNameDebounce: null as any
  }),
  computed: {
    $storeData(): IStoreProviderData {
      return this.$storeProviderData();
    },
    basketData(): IBasketProviderData {
      return this.$basketProviderData();
    },
    provisionData(): IProvisionProviderData {
      return this.$provisionProviderData();
    },
    selectedCategory() {
      return _.get(this.$storeData, "selectedCategory", null);
    },
    items() {
      if (!_.get(this.selectedCategory, "products_count", 0)) {
        return _.map(
          this.$storeData.selectedCategory.subcategories,
          subcategory => {
            return {
              ...subcategory,
              route: getCategoryRoute(this, {
                [QUERY_PARAMS.CATEGORY_ID]: `${subcategory.id}`
              })
            };
          }
        );
      }

      return this.$storeData.products;
    },
    productsImageFilter() {
      return _.some(this.$storeData.products, product => {
        return _.get(product, "image");
      });
    },
    showDescription() {
      const { searchValue, products } = this.$storeData;
      if (!!searchValue || (!products.length && !this.items.length))
        return false;
      return this.categoryDescription;
    },
    truncateDesc() {
      return (
        this.$store.getters["brand/config"][
          BrandConfigKeys.SHOP_TRUNCATE_DESCRIPTIONS
        ] ?? true
      );
    },
    showNoResults() {
      const { productsAreFiltering, searchValue, products } = this.$storeData;
      return !productsAreFiltering && !!searchValue && !products.length;
    },
    showNoSellableProducts() {
      if (this.$storeData.hasDacWidget) return false;
      if (this.$storeData.productsAreFiltering) return false;
      if (this.$storeData.searchValue) return false;
      return (
        !this.$storeData.products.length &&
        !_.get(this.selectedCategory, "subcategories", []).length
      );
    },
    itemsLayout() {
      return this.$storeData.products.length < 3
        ? ShopItemsLayout.HORIZONTAL
        : ShopItemsLayout.VERTICAL;
    },
    categoryName() {
      return (
        _.get(this.$storeData, "selectedCategory.name_translated") ||
        _.get(this.$storeData, "selectedCategory.name")
      );
    },
    categoryDescription() {
      return (
        _.get(
          this.$storeData,
          "selectedCategory.short_description_translated"
        ) ||
        _.get(this.$storeData, "selectedCategory.short_description") ||
        _.get(this.$storeData, "selectedCategory.description_translated") ||
        _.get(this.$storeData, "selectedCategory.description")
      );
    },
    authToken() {
      return _.get(
        this.$store,
        `state.auth.${this.context}.token.access_token`,
        ""
      );
    }
  },
  created() {
    this.searchByNameDebounce = _.debounce(this.searchByName, 500);
  },
  methods: {
    reloadData() {
      this.$storeData.productsAreReloading = true;
      this.$storeMethods.loadCategoryProducts(
        this.$storeData.pathCategoryId,
        null
      );
    },
    dacAddToBasket({ _product, sld }) {
      this.addToBasket({
        _product,
        pfields: [{ name: "sld", value: sld }],
        dac: true
      });
    },
    async addToBasket({
      _product,
      pfields = [],
      dac = false
    }: {
      _product: IProduct;
      pfields?: { name: string; value: string }[];
      dac: boolean;
    }) {
      if (!this.selectedCategory?.products_count) return;

      const isTrial = _product.trial_supported;
      const isConfigurable = this.$basketMethods.isConfigurable(_product, dac);
      const isProvisionable =
        await this.$provisionProviderMethods.isProvisionable({
          productId: _product.id,
          clientId: this.basketData.clientId,
          pfields
        });

      // If configurable or provisionable
      if (isConfigurable || isProvisionable || isTrial) {
        const newPFields = {};
        _.forEach(pfields, pfield => {
          newPFields[`${QUERY_PARAMS.PRODUCT_FIELDS}[${pfield.name}]`] =
            pfield.value;
        });

        // Route to product configuration
        return this.$storeMethods.configureProduct(
          _.merge(
            {
              [QUERY_PARAMS.PRODUCT_ID]: _product.id
            },
            newPFields,
            _product.sub_product_id
              ? { [QUERY_PARAMS.SUBPRODUCT_IDS]: _product.sub_product_id }
              : {}
          )
        );
      }

      // Quick add to basket
      const basket = await this.$basketMethods.quickAddToBasket(
        _product,
        false,
        dac
      );

      // Quick update provisioning fields
      await this.$provisionProviderMethods.quickUpdateBasketProductFields({
        basketId: basket.id,
        productId: _product.id,
        basketProductId: _.get(
          _.orderBy(basket.products, ["created_at"], ["desc"])[0], // get id of the just added product
          "id"
        ),
        clientId: basket.client_id,
        pfields,
        ...(dac ? { ignoreValidate: true } : {})
      });

      if (this.isAdmin) {
        await this.$basketMethods.getBasket(true, basket.id);
      } else {
        await this.$basketMethods.getCurrentBasket(true);
      }

      this.$shopMethods.goToBasket(basket.id);

      // Show cross sells
      this.$basketMethods.showCrossSellsModal({
        productId: _product.id,
        isProvisionable: this.$provisionProviderMethods.isProvisionable,
        pfields,
        basketId: basket.id
      });
    },
    searchByName(value) {
      this.$storeMethods.searchByName(value);
    }
  }
});
</script>
